// 觀看端首幀遙測（plan SPEC #media-live-ttff）：一次觀看（LiveView.start() 一次）只回報一筆——
//   出圖 → POST /v1/homes/{home}/vms/sessions/{session}/media-ready {path, first_frame_ms, signaling_ms, ice_ms, local_cand, remote_cand, warm, client, app_version, transport}
//   沒出圖（no_video / timeout / failed / refused / abandoned）→ POST /v1/homes/{home}/vms/live-reports {attempt_id, device_ref, outcome, client, session_id?, …同上計時}
// 都是 best-effort：失敗吞掉、不重試、不影響觀看。計時從 start()（按下）起算，用 LiveView.now() 的單調時鐘。
import { kinioFetch } from './auth';
import { planeBase } from './config';
import type { LiveAttempt, LiveStopReason, LiveView } from './live';

export type LiveOutcome = 'first_frame' | 'no_video' | 'timeout' | 'failed' | 'refused' | 'abandoned';
/** `web` = LS Home 網頁、`vms` = VMS 網頁（平台從 SDK 標頭分不出兩者，一定要自己帶）。 */
export type LiveClient = 'web' | 'vms';
export type LiveCandType = 'host' | 'srflx' | 'prflx' | 'relay';
export type LiveMediaPath = 'lan' | 'direct' | 'relay';

export interface LiveTtffMeta {
  client: LiveClient;
  /** 網頁版本（建置 sha / 版號，≤ 32 字元，超過截掉） */
  appVersion?: string;
}

export interface LiveCands {
  local_cand?: LiveCandType;
  remote_cand?: LiveCandType;
}

/** 平台收的上限（含）；超過或負的不帶（mediastats.MaxLiveMs）。 */
const MAX_LIVE_MS = 120000;

function ms(from: number | undefined, to: number | undefined): number | undefined {
  if (from === undefined || to === undefined) return undefined;
  const v = Math.round(to - from);
  return v >= 0 && v <= MAX_LIVE_MS ? v : undefined;
}

/** 一次觀看的分段時間：signaling = 按下 → answer、ice = answer → connected、first_frame = 按下 → 第一個影格。 */
export function attemptTimings(a: LiveAttempt, firstFrameAt?: number): { first_frame_ms?: number; signaling_ms?: number; ice_ms?: number; warm: boolean } {
  return {
    first_frame_ms: ms(a.startedAt, firstFrameAt),
    signaling_ms: ms(a.startedAt, a.answerAt),
    ice_ms: ms(a.answerAt, a.connectedAt),
    warm: a.warm,
  };
}

const CAND_TYPES = new Set(['host', 'srflx', 'prflx', 'relay']);

/** getStats 的 selected candidate pair 兩端候選型別（量不到回空物件）。 */
export function candTypesFromStats(report: RTCStatsReport | Map<string, Record<string, unknown>>): LiveCands {
  const byId = new Map<string, Record<string, unknown>>();
  (report as Map<string, Record<string, unknown>>).forEach((v, k) => byId.set(k, v));
  let pair: Record<string, unknown> | undefined;
  for (const s of byId.values()) {
    if (s.type === 'transport' && typeof s.selectedCandidatePairId === 'string') pair = byId.get(s.selectedCandidatePairId);
  }
  if (!pair) {
    for (const s of byId.values()) {
      if (s.type === 'candidate-pair' && (s.selected === true || (s.nominated === true && s.state === 'succeeded'))) {
        pair = s;
        break;
      }
    }
  }
  if (!pair) return {};
  const t = (id: unknown): LiveCandType | undefined => {
    const c = typeof id === 'string' ? byId.get(id) : undefined;
    const v = c?.candidateType;
    return typeof v === 'string' && CAND_TYPES.has(v) ? (v as LiveCandType) : undefined;
  };
  const out: LiveCands = {};
  const l = t(pair.localCandidateId);
  const r = t(pair.remoteCandidateId);
  if (l) out.local_cand = l;
  if (r) out.remote_cand = r;
  return out;
}

async function candTypes(lv: LiveView): Promise<LiveCands> {
  const pc = lv.peerConnection;
  if (!pc || typeof pc.getStats !== 'function') return {};
  try {
    return candTypesFromStats(await pc.getStats());
  } catch {
    return {};
  }
}

type FrameVideo = HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number; cancelVideoFrameCallback?: (h: number) => void };

/**
 * 第一個影格畫上去時呼叫 cb 一次：有 requestVideoFrameCallback 用它（真的畫出來），沒有退回 loadeddata。回傳取消函式。
 * 要在設 srcObject 前後掛都可以；同一個 <video> 換串流要重掛。
 */
export function onFirstFrame(video: HTMLVideoElement, cb: () => void): () => void {
  const v = video as FrameVideo;
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    cb();
  };
  if (typeof v.requestVideoFrameCallback === 'function') {
    const h = v.requestVideoFrameCallback(fire);
    return () => {
      done = true;
      v.cancelVideoFrameCallback?.(h);
    };
  }
  const on = () => fire();
  v.addEventListener('loadeddata', on, { once: true });
  return () => {
    done = true;
    v.removeEventListener('loadeddata', on);
  };
}

/** 沒出圖的結果怎麼歸類：錯誤碼 / 停止原因 → outcome（SPEC #media-live-ttff 表）。 */
export function liveOutcomeOf(x: { code?: string | null; stopReason?: LiveStopReason | null; connected?: boolean }): Exclude<LiveOutcome, 'first_frame'> {
  switch (x.stopReason) {
    case 'user_leave':
    case 'background':
    case 'multiview_leave':
    case 'stream_switch':
      return 'abandoned';
    case 'connect_timeout':
      return 'timeout';
  }
  const code = x.code ?? '';
  if (code === 'CONNECT_TIMEOUT') return 'timeout';
  if (code === 'NO_VIDEO') return 'no_video';
  // 設備 / 平台照規則拒絕：名額、方案、隱私模式（設備回 REJECTED + reason）、記憶體不足（RESOURCE_UNAVAILABLE）、沒有權限
  if (/^(VIEWER_LIMIT|MAX_SESSIONS_REACHED|PLAN_LIMIT|REJECTED|PRIVACY|PRIVACY_MODE|RESOURCE_UNAVAILABLE|LOW_MEMORY|OUT_OF_MEMORY|FORBIDDEN|HOME_FORBIDDEN|PERMISSION_DENIED|NOT_ENTITLED|SEAT_REQUIRED|ACCESS_REVOKED|SESSION_REVOKED)$/.test(code))
    return 'refused';
  if (!code && x.connected) return 'no_video';
  return 'failed';
}

function common(meta: LiveTtffMeta): Record<string, unknown> {
  const out: Record<string, unknown> = { client: meta.client, transport: 'webrtc' };
  if (meta.appVersion) out.app_version = meta.appVersion.slice(0, 32);
  return out;
}

function strip(o: Record<string, unknown>): Record<string, unknown> {
  for (const k of Object.keys(o)) if (o[k] === undefined) delete o[k];
  return o;
}

/** media-ready 的 body（outcome 固定 first_frame，不帶）。 */
export function mediaReadyBody(a: LiveAttempt, path: LiveMediaPath, firstFrameAt: number, cands: LiveCands, meta: LiveTtffMeta): Record<string, unknown> {
  return strip({ path, ...attemptTimings(a, firstFrameAt), ...cands, ...common(meta) });
}

/** live-reports 的 body。 */
export function liveReportBody(a: LiveAttempt, deviceRef: string, outcome: Exclude<LiveOutcome, 'first_frame'>, cands: LiveCands, meta: LiveTtffMeta): Record<string, unknown> {
  const t = attemptTimings(a);
  delete t.first_frame_ms;
  return strip({ attempt_id: a.id, device_ref: deviceRef, outcome, session_id: a.sessionId, ...t, ...cands, ...common(meta) });
}

function vmsBase(lv: LiveView): string {
  return `${planeBase(lv.apiEndpoint)}/v1/homes/${encodeURIComponent(lv.homeId)}/vms`;
}

/**
 * 出圖回報：這一次觀看還沒報過、有 hub session 才送。path 量不到（getStats 還沒出 selected pair）就不送——
 * 呼叫端在拿到 path 後再呼叫一次即可（一次觀看只會送出一筆）。回傳有沒有送。
 */
export async function reportLiveFirstFrame(lv: LiveView, path: LiveMediaPath | null | undefined, firstFrameAt: number, meta: LiveTtffMeta): Promise<boolean> {
  const a = lv.attempt;
  if (!a || a.reported || !path || !a.sessionId) return false;
  a.reported = true;
  const body = mediaReadyBody(a, path, firstFrameAt, await candTypes(lv), meta);
  try {
    await kinioFetch(`${vmsBase(lv)}/sessions/${encodeURIComponent(a.sessionId)}/media-ready`, { method: 'POST', body });
  } catch {
    /* 量測用，失敗不影響觀看 */
  }
  return true;
}

/** 沒出圖的結果（這一次觀看還沒報過才送）。要在 dispose / stop 之前呼叫才量得到候選型別。回傳有沒有送。 */
export async function reportLiveOutcome(lv: LiveView, outcome: Exclude<LiveOutcome, 'first_frame'>, meta: LiveTtffMeta): Promise<boolean> {
  const a = lv.attempt;
  if (!a || a.reported) return false;
  a.reported = true;
  // 先同步拍下 attempt（dispose 之後 LiveView 的 pc 就沒了；候選型別盡量量，量不到不帶）
  const snap = { ...a };
  const cands = await candTypes(lv);
  try {
    await kinioFetch(`${vmsBase(lv)}/live-reports`, { method: 'POST', body: liveReportBody(snap, lv.deviceRef, outcome, cands, meta) });
  } catch {
    /* 量測用 */
  }
  return true;
}
